import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Download, Database, FileText, HardDrive } from "lucide-react";

function fmtBytes(n) {
  if (!n) return "0 B";
  const units = ["B","KB","MB","GB","TB"];
  let i = 0; let v = n;
  while (v >= 1024 && i < units.length-1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`;
}

export default function DatabaseManager() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/admin/db/stats").then(r => setData(r.data)); }, []);

  const exportDb = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/db/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${r.data.tenant_id}_export_${new Date().toISOString().slice(0,10)}.json`;
      a.click();
    } finally { setBusy(false); }
  };

  if (!data) return <div className="p-6 text-ink-soft">Loading…</div>;

  return (
    <div className="p-6 space-y-5" data-testid="db-manager-root">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="section-label">Admin · Database</div>
          <h1 className="text-2xl font-bold tracking-tight mt-1">Database Manager</h1>
          <p className="text-sm text-ink-soft font-mono">{data.database_name}</p>
        </div>
        <button data-testid="db-export-button" disabled={busy} onClick={exportDb} className="btn btn-primary">
          <Download size={14}/> {busy ? "Exporting…" : "Export JSON"}
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { l: "Data size", v: fmtBytes(data.data_size_bytes), i: Database },
          { l: "Storage size", v: fmtBytes(data.storage_size_bytes), i: HardDrive },
          { l: "Index size", v: fmtBytes(data.index_size_bytes), i: HardDrive },
          { l: "Objects", v: data.object_count.toLocaleString(), i: FileText },
        ].map(m => {
          const Icon = m.i;
          return (
            <div key={m.l} className="card p-4">
              <div className="flex items-center justify-between">
                <div className="section-label">{m.l}</div>
                <div className="h-7 w-7 rounded-md bg-brand text-white flex items-center justify-center"><Icon size={14}/></div>
              </div>
              <div className="metric-value">{m.v}</div>
            </div>
          );
        })}
      </div>

      <div className="card p-4" data-testid="db-evidence-card">
        <div className="section-label">Evidence storage</div>
        <div className="mt-1 text-sm">
          <span className="font-mono">{data.evidence.file_count}</span> files · {fmtBytes(data.evidence.size_bytes)} ·
          <span className="ml-1 text-ink-soft font-mono">{data.evidence.path}</span>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-border section-label">Collections</div>
        <table className="dense">
          <thead><tr><th>Collection</th><th className="text-right">Documents</th><th className="text-right">Size</th><th className="text-right">Storage</th><th className="text-right">Avg obj</th><th className="text-right">Indexes</th></tr></thead>
          <tbody>
            {data.collections.map(c => (
              <tr key={c.name} data-testid={`coll-${c.name}`}>
                <td className="font-mono text-xs">{c.name}</td>
                <td className="num">{c.count.toLocaleString()}</td>
                <td className="num">{fmtBytes(c.size_bytes)}</td>
                <td className="num">{fmtBytes(c.storage_bytes)}</td>
                <td className="num">{c.avg_obj_size} B</td>
                <td className="num">{c.indexes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
